/**
 * nettyService.js
 * 
 * Client-side service for fetching properties from Supabase,
 * filtered by site_target. Used by both SCBI and VanessaTancredi frontends.
 * 
 * Usage:
 *   import { fetchProperties, fetchPropertyById } from '../lib/nettyService';
 *   const properties = await fetchProperties('scbi');
 */

import { supabase } from './supabase';

/**
 * Fetch all active properties for a given site target.
 * 
 * @param {string} siteTarget - 'scbi' | 'vanessa' | 'avantis'
 * @param {object} options - Optional filters
 * @param {string} options.type - 'Maison' | 'Appartement' | 'Terrain' | null
 * @param {string} options.status - 'Vente' | 'Location' | null
 * @param {number} options.maxPrice - Maximum price
 * @param {number} options.minPrice - Minimum price
 * @param {string} options.city - City name filter
 */
export async function fetchProperties(siteTarget, options = {}) {
    let query = supabase
        .from('properties')
        .select(`
      id, netty_ref, netty_ref_display, site_target,
      title, price, type, status, marketing_status,
      surface, surface_terrain, rooms, nb_pieces, nb_sdb,
      city, zip_code, address, country,
      image_url, images, features,
      dpe_energy, dpe_ges, dpe_state,
      agent_lastname, agent_firstname, agent_phone, agent_photo, agent_email,
      agency_name, agency_phone,
      latitude, longitude,
      sous_type, is_copro, honoraires_charge, georisque,
      date_online, synced_at, created_at
    `)
        .eq(`is_published_${siteTarget}`, true)

        .not('marketing_status', 'in', '("Vendu","Loué")')
        .order('date_online', { ascending: false, nullsFirst: false });

    // Optional filters
    if (options.type) query = query.eq('type', options.type);
    if (options.status) query = query.eq('status', options.status);
    if (options.maxPrice) query = query.lte('price', options.maxPrice);
    if (options.minPrice) query = query.gte('price', options.minPrice);
    if (options.city) query = query.ilike('city', `%${options.city}%`);

    const { data, error } = await query;

    if (error) {
        console.error('[nettyService] fetchProperties error:', error);
        return null;
    }

    return data;
}

/**
 * Fetch a single property by UUID (Supabase ID) or Netty reference.
 * 
 * @param {string} id - UUID from Supabase OR netty_ref string
 * @param {string} siteTarget - 'scbi' | 'vanessa' | 'avantis'
 */
export async function fetchPropertyById(id, siteTarget) {
    // Detect if this looks like a UUID or a Netty ref
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

    let query = supabase
        .from('properties')
        .select('*')
        .eq(`is_published_${siteTarget}`, true);



    if (isUUID) {
        query = query.eq('id', id);
    } else {
        // Try matching by netty_ref or netty_ref_display
        query = query.or(`netty_ref.eq.${id},netty_ref_display.eq.${id}`);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
        console.error('[nettyService] fetchPropertyById error:', error);
        return null;
    }

    return data;
}

/**
 * Manually trigger a sync of the Netty feed via the Edge Function.
 * Requires the user to be authenticated as admin.
 * 
 * @returns {object} Sync result with routing statistics
 */
export async function triggerNettySync() {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
        throw new Error('Authentication required to trigger sync');
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(`${supabaseUrl}/functions/v1/sync-netty-feed`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const err = await response.text();
        throw new Error(`Sync failed: ${response.status} - ${err}`);
    }

    return response.json();
}

/**
 * Get the display name for an agent, falling back to agency name.
 * Useful for the contact card in PropertyDetail.
 * 
 * @param {object} property - A property row
 */
export function getAgentDisplayInfo(property) {
    if (!property) return null;

    const hasAgent = property.agent_lastname || property.agent_firstname;

    return {
        name: hasAgent
            ? `${property.agent_firstname ?? ''} ${property.agent_lastname ?? ''}`.trim()
            : property.agency_name ?? 'AVANTIS IMMOBILIER',
        phone: property.agent_phone ?? property.agency_phone,
        email: property.agent_email,
        photo: property.agent_photo,
        isAgent: !!hasAgent,
    };
}

/**
 * Format a Netty reference for display.
 * Strips the company suffix for cleaner display.
 * e.g. "VM1592-BORBICONI57840" → "VM1592"
 */
export function formatNettyRef(nettyRefDisplay) {
    if (!nettyRefDisplay) return null;
    return nettyRefDisplay.split('-')[0];
}
